import { request } from '@/api/client';
import { requestWithAttachments } from '@/api/compat';
import {
  CREATE_WIDGET_TICKET,
  MARK_WIDGET_TICKET_READ,
  MY_TICKETS,
  SEND_WIDGET_MESSAGE,
  SEND_WIDGET_MESSAGE_WITHOUT_ATTACHMENTS,
  TICKET_MESSAGES,
  TICKET_MESSAGES_WITHOUT_ATTACHMENTS,
} from '@/api/documents';
import { captureContext } from '@/api/host-context';
import { withSession } from '@/api/session';
import type { WidgetMessage, WidgetTicket } from '@/api/types';

/**
 * The contact's threads, newest activity first.
 *
 * The API caps this at 50 and accepts an `offset` we deliberately do not send:
 * a "load more" is out of scope for the skeleton. The consequence is real and
 * worth stating rather than discovering — a contact with more than fifty
 * threads sees only the fifty most recently active, with nothing in the UI
 * saying so. When that becomes worth fixing, a full page of 50 means "ask again
 * with offset + 50" and an empty array means "that was all".
 */
export const fetchMyTickets = async (): Promise<WidgetTicket[]> => {
  const data = await withSession((token) => request<{ myTickets: WidgetTicket[] }>(MY_TICKETS, {}, token));

  return data.myTickets;
};

/**
 * One thread's turns, oldest first.
 *
 * The API returns at most the newest 200. Past that a thread loses its
 * beginning, not its end — deliberate on their side, so an agent's latest reply
 * can never be what gets cut. No pagination here either; see
 * {@link fetchMyTickets}.
 */
export const fetchTicketMessages = async (ticketId: string): Promise<WidgetMessage[]> => {
  const data = await withSession((token) => requestWithAttachments<{ ticketMessages: TurnAsSent[] }>(
    { current: TICKET_MESSAGES, previous: TICKET_MESSAGES_WITHOUT_ATTACHMENTS },
    { ticketId },
    token,
  ));

  return data.ticketMessages.map(withAttachments);
};

/** A turn as an API of either revision sends it: the older one names no files. */
type TurnAsSent = Omit<WidgetMessage, 'attachments'> & Partial<Pick<WidgetMessage, 'attachments'>>;

const withAttachments = (turn: TurnAsSent): WidgetMessage => ({ ...turn, attachments: turn.attachments ?? [] });

/**
 * The attachment field of an input, present only when there is something to
 * attach. An API that predates attachments refuses an input naming a field it
 * does not have, so a message with no file must not name one.
 */
const attached = (attachmentIds: string[]): { attachmentIds?: string[] } =>
  (attachmentIds.length > 0 ? { attachmentIds } : {});

export const createTicket = async (
  title: string,
  body: string,
  attachmentIds: string[] = [],
): Promise<WidgetTicket> => {
  // Read once, at the press of Send, and reused if the call is replayed after
  // a renewal: the context belongs to the moment the visitor wrote, not to
  // whichever attempt happened to reach the server.
  const context = captureContext();

  const data = await withSession((token) => request<{ createWidgetTicket: WidgetTicket }>(
    CREATE_WIDGET_TICKET,
    { input: { title, body, ...attached(attachmentIds), ...(context ? { context } : {}) } },
    token,
  ));

  return data.createWidgetTicket;
};

export const sendMessage = async (
  ticketId: string,
  body: string,
  attachmentIds: string[] = [],
): Promise<WidgetMessage> => {
  const data = await withSession((token) => requestWithAttachments<{ sendWidgetMessage: TurnAsSent }>(
    { current: SEND_WIDGET_MESSAGE, previous: SEND_WIDGET_MESSAGE_WITHOUT_ATTACHMENTS },
    { input: { ticketId, body, ...attached(attachmentIds) } },
    token,
  ));

  return withAttachments(data.sendWidgetMessage);
};

/**
 * Report that this thread is on screen, drawn up to and including
 * `lastMessageId`.
 *
 * The id is the last turn the panel actually rendered, never "the latest" — a
 * reply that arrived after the fetch has been shown to nobody, and the API
 * takes this as the visitor having read everything up to it.
 */
export const markTicketRead = async (ticketId: string, lastMessageId: string): Promise<void> => {
  await withSession((token) => request<{ markWidgetTicketRead: boolean }>(
    MARK_WIDGET_TICKET_READ,
    { input: { ticketId, lastMessageId } },
    token,
  ));
};
